<template>
  <TransitionRoot as="template" :show="isOpen">
    <Dialog class="relative z-30" @close="close">
      <TransitionChild
        as="template"
        enter="ease-in-out duration-500"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in-out duration-500"
        leave-from="opacity-100"
        leave-to="opacity-0">
        <div class="fixed inset-0 bg-neutral-900/75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-hidden">
        <div class="absolute inset-0 overflow-hidden">
          <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <TransitionChild
              as="template"
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enter-from="translate-x-full"
              enter-to="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leave-from="translate-x-0"
              leave-to="translate-x-full">
              <DialogPanel class="pointer-events-auto w-screen max-w-md">
                <div
                  class="flex h-full flex-col overflow-y-scroll overflow-x-hidden bg-white dark:bg-neutral-800 pb-6 shadow-xl">
                  <div class="sticky top-0 px-4 sm:px-6 bg-white dark:bg-neutral-800 z-20 pt-6">
                    <div class="flex items-start justify-between">
                      <DialogTitle class="text-base font-semibold leading-6 text-neutral-900 dark:text-white">
                        Notifications
                      </DialogTitle>
                      <div class="ml-3 flex gap-6 h-7 items-center">
                        <button
                          type="button"
                          class="w-6 h-6 flex items-center justify-center relative rounded-md bg-white dark:bg-neutral-800 text-neutral-400 hover:text-neutral-500 dark:hover:text-white dark:ring-offset-neutral-800 focus:outline-hidden focus:ring-2 focus:ring-accent-500 focus:ring-offset-2"
                          @click="authUser.mute_notifications = !authUser.mute_notifications">
                          <span class="absolute -inset-2.5" />
                          <span class="sr-only">Toggle Mute</span>
                          <i
                            class="fa-sharp fa-fw"
                            :class="authUser.mute_notifications ? 'fa-volume-slash' : 'fa-volume'"
                            aria-hidden="true"></i>
                        </button>

                        <button
                          type="button"
                          class="w-6 h-6 flex items-center justify-center relative rounded-md bg-white dark:bg-neutral-800 text-neutral-400 hover:text-neutral-500 dark:hover:text-white dark:ring-offset-neutral-800 focus:outline-hidden focus:ring-2 focus:ring-accent-500 focus:ring-offset-2"
                          @click="close">
                          <span class="absolute -inset-2.5" />
                          <span class="sr-only">Close notifications</span>
                          <i class="fa-sharp fa-times fa-fw" aria-hidden="true"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="relative mt-6 flex-1 px-4 sm:px-6">
                    <NtSpacing>
                      <NtSwitch label="Do Not Disturb" name="do_not_disturb" v-model="authUser.do_not_disturb" />
                      <TransitionGroup
                        move-class="transition-all duration-500"
                        enter-active-class="transition-all duration-500"
                        leave-active-class="transition-all duration-200 absolute"
                        enter-from-class="scale-90 opacity-0"
                        leave-to-class="translate-x-64 opacity-0"
                        @before-leave="beforeLeave">
                        <NtNotificationGroup
                          v-for="(items, name) in groups"
                          :key="name"
                          :name="name"
                          :notifications="items"
                          @close="close" />
                      </TransitionGroup>
                    </NtSpacing>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import NtSpacing from '../general/NtSpacing.vue'
import NtSwitch from '../forms/NtSwitch.vue'
import NtNotificationGroup from './NtNotificationGroup.vue'
import { mapActions, mapState } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import { groupBy } from 'lodash'
import { useNotificationStore } from '@/stores/notifications'

export default {
  components: {
    Dialog, // eslint-disable-line vue/no-reserved-component-names
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    NtSpacing,
    NtSwitch,
    NtNotificationGroup
  },
  data() {
    return {
      isOpen: false
    }
  },
  computed: {
    ...mapState(useAuthStore, ['authUser']),
    ...mapState(useNotificationStore, ['notifications', 'displayedNotification']),
    groups() {
      return groupBy(this.notifications, 'data.group')
    }
  },
  methods: {
    ...mapActions(useNotificationStore, ['dismissNotification']),
    open() {
      this.isOpen = true
      this.dismissNotification()
    },
    close() {
      this.isOpen = false
    },
    beforeLeave(el) {
      const { marginLeft, marginTop, width, height } = window.getComputedStyle(el)

      el.style.left = `${el.offsetLeft - parseFloat(marginLeft, 10)}px`
      el.style.top = `${el.offsetTop - parseFloat(marginTop, 10)}px`
      el.style.width = width
      el.style.height = height
    }
  },
  watch: {
    displayedNotification(val) {
      if (val && this.isOpen) {
        setTimeout(() => {
          this.dismissNotification()
        }, 500)
      }
    },
    'authUser.mute_notifications'(val) {
      this.$api.patch(`v3/users/${this.authUser.id}`, {
        mute_notifications: val
      })
    },
    'authUser.do_not_disturb'(val) {
      this.$api.patch(`v3/users/${this.authUser.id}`, {
        do_not_disturb: val
      })
    }
  }
}
</script>
